// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/services/cargaService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import { paramsDaLista, type ListaFiltros, type PaginadoComResumo, type CargaListaResumo } from './listaFiltros';
import type {
  CargaResumo,
  CargaDetalhe,
  IniciarCargaRequest,
  AtualizarCargaRequest,
  RetornarCargaRequest,
} from '../types';
import { paramsConsulta, type FiltroConsultaOperacional } from '../consultas';

const BASE = '/api/cargas';

/** KPIs de GET /api/cargas/resumo — consulta inteira, sem o status. */
export interface CargaKpi {
  total: number;
  montagem: number;
  emRota: number;
  retornadas: number;
  finalizadas: number;
  valorTotal: number;
  valorDivergencia: number;
}

/** Linha da listagem com a placa do veículo e a descrição da rota (o GET /api/cargas as traz). */
export type CargaListagem = CargaResumo & { veiculoDescricao?: string | null; rotaDescricao?: string | null };

/** Filtros da consulta de cargas: busca, status, dtInicio/dtFim (montagem), veiculoId, rotaId, motorista (nome). */
export type FiltroCargas = FiltroConsultaOperacional & { veiculoId?: number; rotaId?: number; motorista?: string };

export const cargaService = {
  /**
   * Lista paginada + `resumo` (KPIs v118). Aceita os filtros da v118 (`decrescente`) e os da
   * paridade (`direcao` e filtros extras) — o servidor entende os dois.
   */
  async getAll(pagina = 1, tamanhoPagina = 20, filtros?: FiltroCargas | ListaFiltros): Promise<PaginadoComResumo<CargaListagem, CargaListaResumo>> {
    const { data } = await apiClient.get<PaginadoComResumo<CargaListagem, CargaListaResumo>>(BASE, {
      params: { ...paramsDaLista(pagina, tamanhoPagina), ...paramsConsulta(filtros as FiltroCargas) },
    });
    return data;
  },

  /** KPIs sobre o filtro inteiro (ignora status). */
  async getResumo(filtros?: FiltroCargas): Promise<CargaKpi> {
    const { status: _s, ordenarPor: _o, direcao: _d, ...resto } = filtros ?? {};
    const { data } = await apiClient.get<CargaKpi>(`${BASE}/resumo`, { params: paramsConsulta(resto) });
    return data;
  },

  async getAbertas(): Promise<CargaResumo[]> {
    const { data } = await apiClient.get<CargaResumo[]>(`${BASE}/abertas`);
    return data;
  },

  async getById(id: number): Promise<CargaDetalhe> {
    const { data } = await apiClient.get<CargaDetalhe>(`${BASE}/${id}`);
    return data;
  },

  async iniciar(request: IniciarCargaRequest): Promise<CargaDetalhe> {
    const { data } = await apiClient.post<CargaDetalhe>(BASE, request);
    return data;
  },

  async update(id: number, request: AtualizarCargaRequest): Promise<CargaResumo> {
    const { data } = await apiClient.put<CargaResumo>(`${BASE}/${id}`, request);
    return data;
  },

  async registrarRetorno(id: number, request: RetornarCargaRequest): Promise<CargaDetalhe> {
    const { data } = await apiClient.put<CargaDetalhe>(`${BASE}/${id}/retorno`, request);
    return data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },
};
